// v3.92 - W3-1-F3: render chunk_excerpt_text under SHOW_YOUR_WORK_LEVEL2_ENABLED gate - 2026-07-02
// v3.90 - W3-1-Step3: wowSourceChunks prop -- render retrieval_method + relevance_score only - 2026-07-02
// v3.78 - GOLD-20-01: Add 'Early signal — verify recommended' label color - 2026-04-18
// v3.77 - SHOW-01: Show Your Work — fetches from ai_traces endpoint - 2026-03-13
//
// Renders an inline expandable "How we found this" panel for any AI-generated
// output (KPI card, recommendation, report card, NLP answer).
//
// Behaviour:
//  - If SHOW_YOUR_WORK_ENABLED flag is false → renders nothing
//  - Fetches trace data lazily (on first expansion click, not on mount) to avoid
//    cold N+1 requests on pages with many cards
//  - 404 or any error → renders nothing (silently hidden)
//  - Level-2 detail (data sources + time period) controlled by
//    SHOW_YOUR_WORK_LEVEL2_ENABLED flag

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { traceService, TraceData, RagChunkRef } from '../services/api';
import {
  SHOW_YOUR_WORK_ENABLED,
  SHOW_YOUR_WORK_LEVEL2_ENABLED,
  WOW_INSIGHTS_ENABLED,
} from '../config/featureFlags';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShowYourWorkProps {
  entityType: string;   // "kpi_card" | "recommendation" | "insight" | "nlp_answer"
  entityId: string;     // matches entity_id in ai_traces table
  // v3.90 - W3-1-Step3: optional WOW source chunks (already stripped to method+score by server)
  wowSourceChunks?: RagChunkRef[];
}

// ---------------------------------------------------------------------------
// Confidence label → colour mapping
// Labels come from the backend — never display raw scores to users
// ---------------------------------------------------------------------------

const LABEL_COLOR: Record<string, string> = {
  'Strong signal':                   '#4CAF50',  // green
  'Good indication':                 '#2196F3',  // blue
  'Early signal':                    '#FF9800',  // amber
  'Early signal — verify recommended': '#FF9800',  // amber — v3.78 GOLD-20-01
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ShowYourWork: React.FC<ShowYourWorkProps> = ({ entityType, entityId, wowSourceChunks }) => {
  const [expanded, setExpanded]       = useState(false);
  const [loading, setLoading]         = useState(false);
  const [trace, setTrace]             = useState<TraceData | null | 'hidden'>(null);
  // null  = not yet fetched
  // TraceData = fetched successfully
  // 'hidden' = 404 / error — panel should not render at all

  const handleToggle = useCallback(async () => {
    if (trace === 'hidden') return;

    // First expansion — fetch trace data
    if (trace === null) {
      setLoading(true);
      try {
        const data = await traceService.getTrace(entityType, entityId);
        if (data === null) {
          setTrace('hidden');
          return;
        }
        setTrace(data);
        setExpanded(true);
      } catch {
        setTrace('hidden');
      } finally {
        setLoading(false);
      }
      return;
    }

    setExpanded(prev => !prev);
  }, [trace, entityType, entityId]);

  // Feature flag gate
  if (!SHOW_YOUR_WORK_ENABLED) return null;

  // After a failed fetch, hide completely
  if (trace === 'hidden') return null;

  const labelColor =
    trace && typeof trace === 'object'
      ? LABEL_COLOR[trace.confidence_label] ?? '#9E9E9E'
      : '#9E9E9E';

  return (
    <Box sx={{ mt: 0.5 }}>

      {/* Trigger — unobtrusive text link with info icon */}
      <Box
        component="button"
        onClick={handleToggle}
        disabled={loading}
        sx={{
          background: 'none',
          border: 'none',
          cursor: loading ? 'default' : 'pointer',
          p: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.4,
          color: 'text.disabled',
          '&:hover:not(:disabled)': { color: 'text.secondary' },
        }}
      >
        {loading ? (
          <CircularProgress size={11} sx={{ color: 'text.disabled' }} />
        ) : (
          <InfoOutlinedIcon sx={{ fontSize: 12 }} />
        )}
        <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>
          How we found this
        </Typography>
      </Box>

      {/* Expandable panel */}
      {trace && typeof trace === 'object' && (
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 0.75,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              maxWidth: 340,
            }}
          >
            {/* Confidence label chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={trace.confidence_label}
                size="small"
                sx={{
                  bgcolor: labelColor,
                  color: '#fff',
                  fontSize: '0.68rem',
                  height: 20,
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>

            {/* Summary headline */}
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ fontSize: '0.72rem', display: 'block', mb: 0.75, lineHeight: 1.4 }}
            >
              {trace.summary_headline}
            </Typography>

            {/* How we found it */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.68rem', display: 'block', mb: SHOW_YOUR_WORK_LEVEL2_ENABLED ? 0.75 : 0, lineHeight: 1.4 }}
            >
              {trace.how_we_found_it}
            </Typography>

            {/* Level-2 detail: data sources + time period */}
            {SHOW_YOUR_WORK_LEVEL2_ENABLED && (
              <>
                {trace.data_sources_used && trace.data_sources_used.length > 0 && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: '0.65rem', display: 'block' }}
                    >
                      Based on
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.68rem', display: 'block' }}
                    >
                      {trace.data_sources_used.join(', ')}
                    </Typography>
                  </Box>
                )}
                {trace.time_period && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: '0.65rem', display: 'block' }}
                    >
                      Period
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.68rem', display: 'block' }}
                    >
                      {trace.time_period}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {/* v3.90 - W3-1-Step3: WOW source chunks — retrieval_method + relevance_score ONLY.
                chunk_id, chunk_excerpt_hash, knowledge_base_id are stripped server-side (§3.12/§3.5)
                and are never present in wowSourceChunks at this point. */}
            {WOW_INSIGHTS_ENABLED && wowSourceChunks && wowSourceChunks.length > 0 && (
              <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: '0.65rem', display: 'block' }}
                >
                  WOW signal
                </Typography>
                {wowSourceChunks.map((chunk, idx) => (
                  <Box key={idx}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.68rem', display: 'block', lineHeight: 1.4 }}
                    >
                      {chunk.retrieval_method} &middot; relevance {Math.round(chunk.relevance_score * 100)}%
                    </Typography>
                    {/* v3.92 - F3: excerpt rendered under Level-2 gate; no internal ids in DOM */}
                    {SHOW_YOUR_WORK_LEVEL2_ENABLED && chunk.chunk_excerpt_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.68rem', display: 'block', fontStyle: 'italic', lineHeight: 1.4 }}
                      >
                        &ldquo;{chunk.chunk_excerpt_text}&rdquo;
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ShowYourWork;
